import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client";
import Rating from "../components/Rating";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Heart, Minus, Plus, Leaf, Flame, ShieldCheck } from "lucide-react";
import { LineSkeleton } from "../components/Skeletons";

export default function ProductDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [tab, setTab] = useState("ingredients");
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const { addToCart } = useCart();
    const { has, toggle } = useWishlist();
    const { user } = useAuth();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/products/${slug}`);
            setProduct(data);
        } catch {
            toast.error("Product not found");
            nav("/products");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [slug]);

    const submitReview = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error("Please sign in to review");
            nav("/login");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/products/${product.id}/reviews`, { rating: reviewRating, comment: reviewComment });
            toast.success("Review added");
            setReviewComment("");
            await load();
        } catch (e) {
            toast.error("Failed to add review");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !product) {
        return (
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-2 gap-10">
                <div className="skeleton aspect-square rounded-3xl" />
                <div className="space-y-4">
                    <LineSkeleton h="h-8" w="w-3/4" />
                    <LineSkeleton h="h-4" w="w-1/3" />
                    <LineSkeleton h="h-20" w="w-full" />
                </div>
            </div>
        );
    }

    const discount = product.original_price
        ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
        : 0;
    const wished = has(product.id);

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
                <div className="relative bg-brand-sand rounded-3xl overflow-hidden aspect-square">
                    <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover" />
                    {discount > 0 && (
                        <span className="absolute top-5 left-5 bg-brand-orange text-white text-sm font-black px-3 py-1.5 rounded-lg">
                            -{discount}% OFF
                        </span>
                    )}
                </div>

                <div>
                    <div className="flex flex-wrap gap-2 mb-4">
                        {product.badges?.map((b) => (
                            <span key={b} className="bg-brand-sand text-brand-ink px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                {b}
                            </span>
                        ))}
                    </div>
                    <h1 data-testid="product-title" className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[1] text-brand-ink">
                        {product.name}
                    </h1>
                    <div className="mt-3 flex items-center gap-3">
                        <Rating value={product.rating} count={product.reviews_count} />
                        <span className="text-sm text-brand-moss">• {product.pack_size}</span>
                    </div>
                    <div className="mt-6 flex items-end gap-3">
                        <span className="font-heading font-black text-4xl">₹{product.price}</span>
                        {product.original_price && (
                            <span className="text-lg line-through text-brand-moss mb-1">₹{product.original_price}</span>
                        )}
                    </div>
                    <p className="mt-6 text-brand-moss leading-relaxed">{product.description}</p>

                    <div className="mt-8 flex items-center gap-4">
                        <div className="inline-flex items-center bg-brand-cream rounded-xl border border-brand-line">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="pdp-qty-dec" className="w-12 h-12 flex items-center justify-center hover:text-brand-orange">
                                <Minus size={16} />
                            </button>
                            <span data-testid="pdp-qty" className="w-10 text-center font-bold">{qty}</span>
                            <button onClick={() => setQty(qty + 1)} data-testid="pdp-qty-inc" className="w-12 h-12 flex items-center justify-center hover:text-brand-orange">
                                <Plus size={16} />
                            </button>
                        </div>
                        <button
                            onClick={() => addToCart(product, qty)}
                            data-testid="pdp-add-to-cart"
                            className="flex-1 bg-brand-orange text-white font-bold py-4 rounded-xl btn-lift hover:bg-brand-orangeDark shadow-[0_4px_14px_0_rgba(255,107,53,0.39)]"
                        >
                            Add to Cart — ₹{(product.price * qty).toFixed(0)}
                        </button>
                        <button
                            onClick={() => toggle(product)}
                            data-testid="pdp-wishlist"
                            className="w-12 h-12 rounded-xl border-2 border-brand-ink flex items-center justify-center hover:bg-brand-ink hover:text-white transition"
                            aria-label="Wishlist"
                        >
                            <Heart size={18} className={wished ? "fill-brand-orange text-brand-orange" : ""} />
                        </button>
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-3">
                        <div className="bg-white border border-brand-line rounded-xl p-4 text-center">
                            <Leaf size={18} className="mx-auto text-brand-sage mb-1" />
                            <div className="text-[11px] font-bold uppercase tracking-wider">Natural</div>
                        </div>
                        <div className="bg-white border border-brand-line rounded-xl p-4 text-center">
                            <Flame size={18} className="mx-auto text-brand-orange mb-1" />
                            <div className="text-[11px] font-bold uppercase tracking-wider">Air Roasted</div>
                        </div>
                        <div className="bg-white border border-brand-line rounded-xl p-4 text-center">
                            <ShieldCheck size={18} className="mx-auto text-brand-ink mb-1" />
                            <div className="text-[11px] font-bold uppercase tracking-wider">Clean label</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABS */}
            <div className="mt-20">
                <div className="flex gap-3 border-b border-brand-line overflow-x-auto hide-scrollbar">
                    {["ingredients", "nutrition", "reviews"].map((t) => (
                        <button
                            key={t}
                            data-testid={`tab-${t}`}
                            onClick={() => setTab(t)}
                            className={`px-6 py-4 text-sm font-bold uppercase tracking-wider whitespace-nowrap border-b-2 ${
                                tab === t ? "border-brand-orange text-brand-orange" : "border-transparent text-brand-moss"
                            }`}
                        >
                            {t === "reviews" ? `Reviews (${product.reviews?.length || 0})` : t}
                        </button>
                    ))}
                </div>

                <div className="mt-6">
                    {tab === "ingredients" && (
                        <ul className="list-disc pl-6 space-y-2 text-brand-moss">
                            {product.ingredients?.map((i) => <li key={i}>{i}</li>)}
                        </ul>
                    )}
                    {tab === "nutrition" && (
                        <div className="bg-white border border-brand-line rounded-2xl overflow-hidden max-w-lg">
                            <div className="bg-brand-ink text-white px-6 py-3 text-xs font-bold uppercase tracking-wider">
                                Per 100g
                            </div>
                            <div className="divide-y divide-brand-line">
                                {Object.entries(product.nutrition || {}).map(([k, v]) => (
                                    <div key={k} className="flex justify-between px-6 py-3">
                                        <span className="font-bold">{k}</span>
                                        <span>{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {tab === "reviews" && (
                        <div className="space-y-6">
                            <form onSubmit={submitReview} className="bg-white border border-brand-line rounded-2xl p-6 max-w-xl">
                                <h4 className="font-heading font-bold text-xl mb-4">Write a review</h4>
                                <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setReviewRating(n)}
                                            data-testid={`review-star-${n}`}
                                            className="text-2xl"
                                        >
                                            <span className={n <= reviewRating ? "text-brand-orange" : "text-brand-line"}>★</span>
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="Share your thoughts..."
                                    required
                                    data-testid="review-comment"
                                    className="w-full bg-brand-cream border-none rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-orange min-h-[100px]"
                                />
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="review-submit"
                                    className="mt-4 bg-brand-ink text-white px-6 py-3 rounded-xl font-bold disabled:opacity-50 hover:bg-brand-orange transition"
                                >
                                    {submitting ? "Submitting..." : "Submit review"}
                                </button>
                            </form>
                            {product.reviews?.length ? (
                                <div className="space-y-4">
                                    {product.reviews.map((r) => (
                                        <div key={r.id} className="bg-white border border-brand-line rounded-2xl p-5">
                                            <div className="flex justify-between mb-2">
                                                <strong>{r.user_name}</strong>
                                                <Rating value={r.rating} showCount={false} />
                                            </div>
                                            <p className="text-brand-moss text-sm">{r.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-brand-moss">No reviews yet. Be the first!</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
